import type { WorkflowStage } from '../../stores/utils/workflow-stage';

/**
 * Project Bobcat (guided setup, on when the master, Validate and Observe are on) — the guided setup after Create, as
 * the prototype runs it (`prostudio-build-flow-layouts.html`, `STAGE_META`,
 * `renderSetupNav`). Start is step 1 and done by the time a solution exists;
 * the five stages are steps 2 to 6. `reached` is the index of the furthest
 * stage reached.
 */
export const SETUP_STAGES: WorkflowStage[] = [
  'design',
  'build',
  'validate',
  'deploy',
  'observe',
];

export const STAGE_LABEL: Record<WorkflowStage, string> = {
  design: 'Design',
  build: 'Build',
  validate: 'Validate',
  deploy: 'Deploy',
  observe: 'Observe',
};

/** The coach's line for each stage — the prototype's copy. */
export const STAGE_COACH: Record<WorkflowStage, string> = {
  design:
    'We drafted your agents, channels and global instructions from what you told us. Check them, then move on.',
  build:
    'Here is the workflow we built. Open any agent to adjust its nodes, or drop in microagents from the Resource Library.',
  validate:
    'Talk to it in Test the way a customer would, or simulate a scenario. Every run is scored before you publish.',
  deploy: 'Publish to Sandbox first. Promote to Live once it looks right.',
  observe:
    'Its dashboards live in Analytics, and any of them opens from here. That is setup done: every stage stays one tab away.',
};

export const stageIndex = (stage: WorkflowStage): number =>
  SETUP_STAGES.indexOf(stage);

/** How a stage tab reads during setup. */
export interface SetupNavItem {
  /** 2–6: the step number shown on the tab. */
  step: number;
  /** Reached before, and not the stage you are on: a check. */
  done: boolean;
  /** Not reached yet: shut, with why. */
  locked: boolean;
  title?: string;
}

export const setupNavItem = (
  stage: WorkflowStage,
  current: WorkflowStage,
  reached: number
): SetupNavItem => {
  const i = stageIndex(stage);
  const locked = i > reached;
  return {
    step: i + 2,
    done: i < reached && stage !== current,
    locked,
    ...(locked
      ? { title: `Unlocks after ${STAGE_LABEL[SETUP_STAGES[i - 1]]}` }
      : {}),
  };
};

/** What the coach card says, and where its button goes. */
export interface CoachState {
  step: string;
  /** Six dots: Start (always done) and the five stages. */
  dots: boolean[];
  title: string;
  text: string;
  /** The button's label; `next` is where it goes, null to finish. */
  action: string;
  next: WorkflowStage | null;
}

export const coachState = (
  current: WorkflowStage,
  reached: number
): CoachState => {
  const at = Math.max(0, stageIndex(current));
  const last = at === SETUP_STAGES.length - 1;
  const behind = at < reached;
  return {
    step: `Guided setup · Step ${at + 2} of 6`,
    dots: [true, ...SETUP_STAGES.map((_, i) => i <= reached)],
    title: STAGE_LABEL[SETUP_STAGES[at]],
    text: STAGE_COACH[SETUP_STAGES[at]],
    // Back on a stage you have passed, the coach takes you back to where
    // you were.
    action: behind
      ? `Back to ${STAGE_LABEL[SETUP_STAGES[reached]]}`
      : last
        ? 'Finish setup'
        : `Continue to ${STAGE_LABEL[SETUP_STAGES[at + 1]]}`,
    next: behind ? SETUP_STAGES[reached] : last ? null : SETUP_STAGES[at + 1],
  };
};

/** Publish belongs to Deploy, so setup keeps it out of the header until then. */
export const showsPublish = (reached: number | null): boolean =>
  reached === null || reached >= stageIndex('deploy');

/**
 * Guided setup walks through every stage in order, so it runs only when the
 * master (which shows the five stages) and Validate and Observe are all on;
 * otherwise it would stop at a stage that shows "coming soon" (PSTUDIO-4583,
 * release page "Bobcat October Release").
 */
export const guidedSetupAllowed = ({
  master,
  validate,
  observe,
}: {
  master: boolean;
  validate: boolean;
  observe: boolean;
}): boolean => master && validate && observe;
